import Phaser from 'phaser';

interface MovableObject {
  x: number;
  y: number;
}

export enum Direction {
  Up = 'up',
  Down = 'down',
  Left = 'left',
  Right = 'right',
}

export const DIRECTIONS = [Direction.Up, Direction.Down, Direction.Left, Direction.Right];

export function oppositeDirection(direction: Direction): Direction {
  switch (direction) {
    case Direction.Left:
      return Direction.Right;
    case Direction.Right:
      return Direction.Left;
    case Direction.Up:
      return Direction.Down;
    case Direction.Down:
      return Direction.Up;
  }
}

export function relativeDirection(source: MovableObject, target: MovableObject): Direction {
  // GameObjects use an inverted Y axis so positive angles rotate clockwise rather than counterclockwise
  const degrees = Phaser.Math.RadToDeg(Phaser.Math.Angle.Between(source.x, source.y, target.x, target.y));
  if (degrees >= 0) {
    if (degrees < 45) {
      return Direction.Right;
    } else if (degrees < 135) {
      return Direction.Down;
    } else {
      return Direction.Left;
    }
  } else {
    if (degrees > -45) {
      return Direction.Right;
    } else if (degrees > -135) {
      return Direction.Up;
    } else {
      return Direction.Left;
    }
  }
}

export function withinFacingCone(source: MovableObject, target: MovableObject, direction: Direction, range: number) {
  // 0 degrees == right
  let degrees = Phaser.Math.RadToDeg(Phaser.Math.Angle.Between(source.x, source.y, target.x, target.y));

  // Rotate the angle so that 0 axis (currently pointing right) faces towards the given direction
  switch (direction) {
    case Direction.Up:
      degrees += 90;
      break;
    case Direction.Left:
      degrees += 180;
      break;
    case Direction.Down:
      degrees -= 90;
      break;
  }

  // Wrap back to a 180 to -180 range
  degrees = Phaser.Math.Angle.WrapDegrees(degrees);

  // Check if angle is within a cone centered on 0 degrees and within the given angle range
  return degrees <= range / 2 && degrees >= -range / 2;
}

export function clockwise(direction: Direction): Direction {
  switch (direction) {
    case Direction.Up:
      return Direction.Right;
    case Direction.Right:
      return Direction.Down;
    case Direction.Down:
      return Direction.Left;
    case Direction.Left:
      return Direction.Up;
  }
}

export function counterClockwise(direction: Direction): Direction {
  switch (direction) {
    case Direction.Up:
      return Direction.Left;
    case Direction.Left:
      return Direction.Down;
    case Direction.Down:
      return Direction.Right;
    case Direction.Right:
      return Direction.Up;
  }
}

export function directionalVelocity(velocity: number, direction: Direction): [number, number] {
  switch (direction) {
    case Direction.Left:
      return [-1 * velocity, 0];
    case Direction.Up:
      return [0, -1 * velocity];
    case Direction.Right:
      return [velocity, 0];
    case Direction.Down:
      return [0, velocity];
  }
}

export function relativeMove(object: MovableObject, direction: Direction, distance: number) {
  switch (direction) {
    case Direction.Left:
      object.x -= distance;
      return;
    case Direction.Right:
      object.x += distance;
      return;
    case Direction.Up:
      object.y -= distance;
      return;
    case Direction.Down:
      object.y += distance;
      return;
  }
}

export function gridMove(x: number, y: number, direction: Direction, distance = 1): [number, number] {
  switch (direction) {
    case Direction.Left:
      return [x - distance, y];
    case Direction.Right:
      return [x + distance, y];
    case Direction.Up:
      return [x, y - distance];
    case Direction.Down:
      return [x, y + distance];
  }
}

export function setVelocityTowards(body: Phaser.Physics.Arcade.Body, direction: Direction, speed: number) {
  switch (direction) {
    case Direction.Left:
      body.setVelocity(-speed, 0);
      return;
    case Direction.Right:
      body.setVelocity(speed, 0);
      return;
    case Direction.Up:
      body.setVelocity(0, -speed);
      return;
    case Direction.Down:
      body.setVelocity(0, speed);
      return;
  }
}

export enum Orientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export function getOrientation(direction: Direction) {
  switch (direction) {
    case Direction.Up:
    case Direction.Down:
      return Orientation.Vertical;
    default:
      return Orientation.Horizontal;
  }
}

/**
 * Returns the number of pixels to move something at a rate of pixelsPerSecond
 * over a period of delta milliseconds.
 */
export function pixelDiff(pixelsPerSecond: number, deltaMs: number): number {
  return pixelsPerSecond * (deltaMs / 1000);
}

/** Choose a single value from the given list randomly and return it. */
export function randomChoice<T>(list: T[]): T {
  const index = Math.floor(Math.random() * list.length);
  return list[index];
}

export function randomRange(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

export function weightedIndex<T>(choices: [T, number][]) {
  const weightSums = [];
  let weightSum = 0;
  for (let index = 0; index < choices.length; index++) {
    const [_choice, weight] = choices[index];
    weightSum += weight;
    weightSums.push(weightSum);
  }

  const chosenWeight = randomRange(0, weightSum);
  return weightSums.findIndex((w) => w >= chosenWeight);
}

export function weightedChoice<T>(choices: [T, number][]): T {
  return choices[weightedIndex(choices)][0];
}

export class StateMachine<T extends State> {
  initialState: string;
  possibleStates: { [key: string]: T };
  stateArgs: any[];
  state: string | null;
  stateStack: string[] = [];

  /** Nonce incremented once per transition; useful when we need to detect that a transition happened. */
  protected _transitionNonce = 0;

  /**
   * Acts as a lock to prevent running updates when a transition is pending.
   * This can happen when transitioning to a state with an async `handleEntered`
   * because the game loop may call `update` while `transition` is awaiting the
   * `handleExited` handler of the previous state.
   */
  private pendingTransition = false;

  constructor(initialState: string, possibleStates: { [key: string]: T }, stateArgs: any[] = []) {
    this.initialState = initialState;
    this.possibleStates = possibleStates;
    this.stateArgs = stateArgs;
    this.state = null;

    // State instances get access to the state machine via this.stateMachine.
    // This is annoyingly implicit, but the alternative is fucking up a bunch
    // of method signatures that won't otherwise use this.
    // Useful for triggering a transition outside of `execute`.
    for (const state of Object.values(this.possibleStates)) {
      state.stateMachine = this;
      state.init(...this.stateArgs);
    }
  }

  update(...updateArgs: any[]) {
    if (this.state === null) {
      this.state = this.initialState;
      this.possibleStates[this.state].handleEntered(...this.stateArgs);
    }

    if (this.pendingTransition) {
      return;
    }

    // State function returns the state to transition to.
    // Transitions happen instantly rather than next-frame, so we need
    // to loop through until we don't transition.
    while (true) {
      // eslint-disable-line no-constant-condition
      const newState = this.possibleStates[this.state].update(...this.stateArgs, ...updateArgs);
      if (newState && typeof newState === 'string') {
        this.transition(newState);
      } else {
        break;
      }
    }
  }

  /** Nonce incremented once per transition; useful when we need to detect that a transition happened. */
  get transitionNonce() {
    return this._transitionNonce;
  }

  getStateObject() {
    if (this.state === null) {
      return null;
    }

    return this.possibleStates[this.state];
  }

  async transition(newState: string, ...enterArgs: any[]) {
    if (!(newState in this.possibleStates)) {
      throw Error(`Invalid state ${newState}`);
    }

    this.pendingTransition = true;
    this._transitionNonce++;

    if (this.state) {
      await this.possibleStates[this.state].handleExited(...this.stateArgs);
    }
    this.state = newState;
    await this.possibleStates[this.state].handleEntered(...this.stateArgs, ...enterArgs);

    this.pendingTransition = false;
  }

  async pushState(newState: string, ...enterArgs: any[]) {
    if (this.state) {
      this.stateStack.push(this.state);
    }

    return this.transition(newState, ...enterArgs);
  }

  async popState(...enterArgs: any[]) {
    const newState = this.stateStack.pop();
    if (!newState) {
      throw new Error('Cannot pop state; state stack is empty.');
    }

    return this.transition(newState, ...enterArgs);
  }

  addStates(states: { [key: string]: T }) {
    this.possibleStates = Object.assign(this.possibleStates, states);

    for (const state of Object.values(states)) {
      state.stateMachine = this;
      state.init(...this.stateArgs);
    }
  }
}

export class State {
  _stateMachine!: StateMachine<any>;

  init(..._args: any[]) {}

  handleEntered(..._args: any[]): void | Promise<any> {}

  handleExited(..._args: any[]): void | Promise<any> {}

  update(..._args: any[]): unknown {
    return null;
  }

  get stateMachine() {
    return this._stateMachine;
  }

  set stateMachine(value: StateMachine<any>) {
    this._stateMachine = value;
  }

  async transition(newState: string, ...args: any[]) {
    return this.stateMachine.transition(newState, ...args);
  }

  async pushState(newState: string, ...enterArgs: any[]) {
    return this.stateMachine.pushState(newState, ...enterArgs);
  }

  async popState(...enterArgs: any[]) {
    return this.stateMachine.popState(...enterArgs);
  }
}

/**
 * A reusable state class that waits for a specified amount of frames.
 * Retains progress if interrupted by another state transition.
 */
export class WaitState extends State {
  currentDelay: number | null = null;

  constructor(
    /** Number of frames to wait. If two values are given, picks a random amount of frames between the two. */
    public delay: number | [number, number],
    /** State to transition to after waiting. If none is given, will call popState after waiting. */
    private nextState?: string,
  ) {
    super();
  }

  get hasRemainingDelay() {
    return this.currentDelay !== null;
  }

  handleEntered(forceResetDelay?: boolean) {
    if (!this.hasRemainingDelay || forceResetDelay) {
      if (Array.isArray(this.delay)) {
        this.currentDelay = randomRange(...this.delay);
      } else {
        this.currentDelay = this.delay;
      }
    }
  }

  update() {
    if (this.currentDelay !== null && this.currentDelay-- < 1) {
      this.currentDelay = null;

      if (this.nextState === undefined) {
        return this.popState();
      }

      return this.transition(this.nextState);
    }
  }
}

/**
 * State base class that allows for checking if the state machine has transitioned away from this state after e.g.
 * an async operation.
 */
export class NonceState extends State {
  protected enterNonce = -1;

  handleEntered(..._args: any[]) {
    this.enterNonce = this.stateMachine.transitionNonce;
    return super.handleEntered(..._args);
  }

  get isSameState() {
    return this.stateMachine.transitionNonce === this.enterNonce;
  }
}

/**
 * A single state object that wraps multiple, composable sub-states. This essentially calls the lifecycle methods on
 * each provided sub-state in the order they were passed in, with a few caveats:
 *
 * - Async enter and exit hooks are run sequentially rather than in parallel; the second handleEntered will not start
 *   until the first has resolved.
 * - If a state transition occurs during an enter, exit, or update hook, the remaining state hooks will _not_ be run.
 *   If you transition in a handleEntered, you are skipping the handleEntered on the rest of the sub-states.
 */
export class MultiState extends State {
  constructor(private states: State[]) {
    super();
  }

  set stateMachine(value: StateMachine<any>) {
    super.stateMachine = value;

    for (const state of this.states) {
      state.stateMachine = value;
    }
  }

  init(...args: any[]) {
    for (const state of this.states) {
      state.init(...args);
    }
  }

  async handleEntered(...args: any[]) {
    const transitionNonce = this.stateMachine.transitionNonce;

    for (const state of this.states) {
      await state.handleEntered(...args);
      if (transitionNonce !== this.stateMachine.transitionNonce) {
        return;
      }
    }
  }

  async handleExited(...args: any[]) {
    const transitionNonce = this.stateMachine.transitionNonce;

    for (const state of this.states) {
      await state.handleExited(...args);
      if (transitionNonce !== this.stateMachine.transitionNonce) {
        return;
      }
    }
  }

  update(...args: any[]) {
    const transitionNonce = this.stateMachine.transitionNonce;

    for (const state of this.states) {
      state.update(...args);
      if (transitionNonce !== this.stateMachine.transitionNonce) {
        return;
      }
    }
  }
}

interface JustDownKey {
  _repeatCounter?: number;
}

type PadName = 'pad1' | 'pad2' | 'pad3' | 'pad4';
type ButtonName =
  | 'A'
  | 'B'
  | 'X'
  | 'Y'
  | 'left'
  | 'right'
  | 'up'
  | 'down'
  | 'L1'
  | 'L2'
  | 'R1'
  | 'R2'
  | 'start'
  | 'select'
  | 'L3'
  | 'R3';

const BUTTON_INDEXES = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  L1: 4,
  R1: 5,
  L2: 6,
  R2: 7,
  select: 8,
  start: 9,
  L3: 10,
  R3: 11,
  up: 12,
  down: 13,
  left: 14,
  right: 15,
};

interface DOMButtonOptions {
  usePointerEnter?: boolean;
}

class DOMButton {
  isDown = false;

  constructor(
    public readonly element: HTMLElement,
    { usePointerEnter = false }: DOMButtonOptions = {},
  ) {
    if (usePointerEnter) {
      element.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        element.releasePointerCapture(event.pointerId);
      });
    }

    element.addEventListener(usePointerEnter ? 'pointerover' : 'pointerdown', (event) => {
      event.preventDefault();
      this.isDown = true;
      element.classList.add('is-down');
    });
    element.addEventListener(usePointerEnter ? 'pointerout' : 'pointerup', (event) => {
      event.preventDefault();
      this.isDown = false;
      element.classList.remove('is-down');
    });
    element.addEventListener('contextmenu', (event) => {
      event.preventDefault();
    });
  }
}

interface ControllerButtonOptions {
  buttons?: ControllerButton[];
  keys?: string[];
  dom?: (HTMLElement | string)[];
  pad1?: ButtonName[];
  pad2?: ButtonName[];
  pad3?: ButtonName[];
  pad4?: ButtonName[];
  usePointerEnter?: boolean;
}

export class ControllerButton {
  buttons: ControllerButton[];
  keys: Phaser.Input.Keyboard.Key[];
  dom: DOMButton[];
  pads: {
    pad1?: Phaser.Input.Gamepad.Button[];
    pad2?: Phaser.Input.Gamepad.Button[];
    pad3?: Phaser.Input.Gamepad.Button[];
    pad4?: Phaser.Input.Gamepad.Button[];
  };

  public enabled = true;

  private _isDown = false;
  private _downLastFrame = false;

  constructor(
    scene: Phaser.Scene,
    { buttons = [], keys = [], dom = [], usePointerEnter = false, ...pads }: ControllerButtonOptions = {},
  ) {
    this.buttons = buttons;

    this.keys = keys
      .map((keyString) => scene.input.keyboard?.addKey(keyString))
      .filter((key): key is Phaser.Input.Keyboard.Key => !!key);

    this.pads = {};
    for (const [padName, buttonNames] of Object.entries(pads)) {
      this.pads[padName as PadName] = buttonNames
        .map((buttonName) => scene.input.gamepad![padName as PadName]?.buttons[BUTTON_INDEXES[buttonName]])
        .filter((button): button is Phaser.Input.Gamepad.Button => !!button);
    }

    this.dom = dom.map((element) => {
      if (typeof element === 'string') {
        const domElement = document.querySelector<HTMLElement>(element);
        if (!domElement) {
          throw new Error(`Couldn't find a dom element matching selector "${element}".`);
        }
        return new DOMButton(domElement, { usePointerEnter });
      }

      return new DOMButton(element, { usePointerEnter });
    });

    scene.events.on('preupdate', () => {
      this.update();
    });

    // Detect newly-added gamepads and hook up their buttons
    scene.input.gamepad!.once('connected', (pad: Phaser.Input.Gamepad.Gamepad) => {
      let padName: PadName | null = null;

      if (scene.input.gamepad?.pad1?.id === pad.id) {
        padName = 'pad1';
      } else if (scene.input.gamepad?.pad2?.id === pad.id) {
        padName = 'pad2';
      } else if (scene.input.gamepad?.pad2?.id === pad.id) {
        padName = 'pad3';
      } else if (scene.input.gamepad?.pad2?.id === pad.id) {
        padName = 'pad4';
      }

      if (padName) {
        const buttonNames = pads[padName];
        if (buttonNames) {
          this.pads[padName] = buttonNames
            .map((buttonName) => scene.input.gamepad![padName!]?.buttons[BUTTON_INDEXES[buttonName]])
            .filter((button): button is Phaser.Input.Gamepad.Button => !!button);
        }
      }
    });
  }

  get isDown() {
    return this._isDown;
  }

  get justDown() {
    return this._isDown && !this._downLastFrame;
  }

  update() {
    this._downLastFrame = this._isDown;
    this._isDown =
      this.enabled &&
      (this.buttons.some((button) => button.isDown) ||
        this.keys.some((key) => key.isDown) ||
        this.dom.some((domButton) => domButton.isDown) ||
        Object.values(this.pads).some((buttons) => buttons.some((button) => button.pressed)));
  }
}

export interface StandardController {
  up: ControllerButton;
  down: ControllerButton;
  left: ControllerButton;
  right: ControllerButton;
  button1: ControllerButton;
  button2: ControllerButton;
  start: ControllerButton;
}

export function makeStandardController(scene: Phaser.Scene) {
  return {
    up: new ControllerButton(scene, { keys: ['UP'], pad1: ['up'], dom: ['#dpad-up'], usePointerEnter: true }),
    down: new ControllerButton(scene, {
      keys: ['DOWN'],
      pad1: ['down'],
      dom: ['#dpad-down'],
      usePointerEnter: true,
    }),
    left: new ControllerButton(scene, {
      keys: ['LEFT'],
      pad1: ['left'],
      dom: ['#dpad-left'],
      usePointerEnter: true,
    }),
    right: new ControllerButton(scene, {
      keys: ['RIGHT'],
      pad1: ['right'],
      dom: ['#dpad-right'],
      usePointerEnter: true,
    }),
    button1: new ControllerButton(scene, { keys: ['SPACE', 'Z', 'D'], pad1: ['A', 'Y'], dom: ['#button-a'] }),
    button2: new ControllerButton(scene, { keys: ['SHIFT', 'X', 'F'], pad1: ['B', 'X'], dom: ['#button-b'] }),
    start: new ControllerButton(scene, { keys: ['ENTER'], pad1: ['start'], dom: ['#button-start'] }),
  };
}

export function justDown(key: Phaser.Input.Keyboard.Key & JustDownKey, repeatDelay?: number, repeatRate: number = 100) {
  const justDown = Phaser.Input.Keyboard.JustDown(key);
  if (repeatDelay === undefined) {
    return justDown;
  }

  if (key._repeatCounter === undefined) {
    key._repeatCounter = 0;
  }

  if (!key.isDown) {
    return false;
  }

  const duration = key.getDuration();
  if (justDown || duration < repeatDelay) {
    key._repeatCounter = 0;
    return justDown;
  }

  if (duration > repeatDelay + repeatRate * key._repeatCounter) {
    key._repeatCounter++;
    return true;
  }

  return false;
}

/** Wait for duration milliseconds and resolve the returned Promise. */
export function wait(scene: Phaser.Scene, duration: number) {
  return new Promise((resolve) => {
    scene.time.delayedCall(duration, resolve);
  });
}

interface TimedPatternOptions {
  /** If set, the pattern will loop back to the start once it is completed. */
  loop?: boolean;
}

export class TimedPattern {
  timerEvent: Phaser.Time.TimerEvent;
  private currentEventIndex = 0;
  loop: boolean;

  constructor(
    public scene: Phaser.Scene,
    public configs: Phaser.Types.Time.TimerEventConfig[],
    options: TimedPatternOptions = {},
  ) {
    this.loop = options.loop ?? false;

    this.timerEvent = scene.time.addEvent({
      ...configs[0],
      paused: true,
      callback: this.makePatternCallback(configs[0]),
    });
  }

  private queueNext(index: number | null = null) {
    this.currentEventIndex = index ?? this.currentEventIndex + 1;

    if (this.configs.length <= this.currentEventIndex) {
      if (this.loop) {
        this.currentEventIndex = 0;
      } else {
        return;
      }
    }

    const nextConfig = this.configs[this.currentEventIndex];
    this.timerEvent = this.scene.time.addEvent({
      ...nextConfig,
      callback: this.makePatternCallback(nextConfig),
    });
  }

  private makePatternCallback(config: Phaser.Types.Time.TimerEventConfig) {
    return () => {
      const result = config.callback?.apply(config.callbackScope, config.args);
      if (result?.type === 'loop') {
        this.queueNext(this.currentEventIndex);
      } else if (result?.type === 'goto') {
        this.queueNext(result.index);
      } else {
        this.queueNext();
      }
    };
  }

  get paused() {
    return this.timerEvent.paused;
  }

  set paused(value: boolean) {
    this.timerEvent.paused = value;
  }

  get timeScale() {
    return this.timerEvent.timeScale;
  }

  set timeScale(value: number) {
    this.timerEvent.timeScale = value;
  }

  reset() {
    this.currentEventIndex = 0;
    this.timerEvent = this.scene.time.addEvent({
      ...this.configs[0],
      paused: true,
      callback: this.makePatternCallback(this.configs[0]),
    });
  }

  static LOOP() {
    return { type: 'loop' };
  }

  static GOTO(index: number) {
    return { type: 'goto', index };
  }
}

/** Play an animation and resolve the returned promise once it completes. */
export function asyncAnimation(
  sprite: Phaser.GameObjects.Sprite,
  keyOrConfig: string | Phaser.Types.Animations.PlayAnimationConfig,
): Promise<void> {
  return new Promise((resolve) => {
    sprite.once('animationcomplete', resolve);
    sprite.play(keyOrConfig);
  });
}

interface TweenPromise extends Promise<void> {
  tween: Phaser.Tweens.Tween;
}

/** Execute a tween and resolve the returned promise once it completes */
export function asyncTween(scene: Phaser.Scene, config: Phaser.Types.Tweens.TweenBuilderConfig): Promise<void> {
  let tween: Phaser.Tweens.Tween | null = null;
  const promise: TweenPromise = new Promise<void>((resolve) => {
    tween = scene.add.tween({
      ...config,
      onComplete(...args) {
        if (config.onComplete) {
          config.onComplete(...args);
        }
        resolve();
      },
    });
  }) as TweenPromise;
  promise.tween = tween!;
  return promise;
}

/** Execute a counter tween and resolve the returned promise once it completes */
export function asyncCounter(scene: Phaser.Scene, config: Phaser.Types.Tweens.NumberTweenBuilderConfig) {
  let tween: Phaser.Tweens.Tween | null = null;
  const promise: TweenPromise = new Promise<void>((resolve) => {
    tween = scene.tweens.addCounter({
      ...config,
      onComplete(...args) {
        if (config.onComplete) {
          config.onComplete(...args);
        }
        resolve();
      },
    });
  }) as TweenPromise;
  promise.tween = tween!;
  return promise;
}

export async function forEachTween<T>(
  scene: Phaser.Scene,
  values: T[],
  frameDuration: number,
  callback: (value: T) => void,
) {
  return asyncCounter(scene, {
    from: 0,
    to: values.length - 1,
    duration: frameDuration * values.length,
    ease(v: number) {
      return Phaser.Math.Easing.Stepped(v, values.length - 1);
    },
    onUpdate(tween) {
      callback(values[Math.floor(tween.getValue()!)]);
    },
  });
}

interface RelativePosition {
  x?: number;
  y?: number;
}

export async function relativePositionTween(
  scene: Phaser.Scene,
  targets: Phaser.GameObjects.Components.Transform[],
  positions: RelativePosition[],
  frameDuration: number,
) {
  const originalPositions = targets.map((target) => ({ x: target.x, y: target.y }));
  await forEachTween(scene, positions, frameDuration, (position) => {
    for (let k = 0; k < targets.length; k++) {
      const target = targets[k];
      target.x = originalPositions[k].x + (position.x ?? 0);
      target.y = originalPositions[k].y + (position.y ?? 0);
    }
  });
}

export enum ShakeAxis {
  X = 'x',
  Y = 'y',
}

export async function shake(
  scene: Phaser.Scene,
  targets: Phaser.GameObjects.Components.Transform[],
  axis: ShakeAxis,
  amounts: number[],
  frameDuration: number,
) {
  return relativePositionTween(
    scene,
    targets,
    amounts.map((amount) => ({ [axis]: amount })),
    frameDuration,
  );
}

export async function flash(
  scene: Phaser.Scene,
  targets: Phaser.GameObjects.Components.Visible[],
  times: number,
  duration: number,
) {
  return asyncCounter(scene, {
    from: 0,
    to: times * 2 - 1,
    duration: duration,
    onUpdate(tween) {
      const visible = Math.floor(tween.getValue()!) % 2 !== 0;
      for (const target of targets) {
        target.setVisible(visible);
      }
    },
    onComplete() {
      for (const target of targets) {
        target.setVisible(true);
      }
    },
  });
}

export function asyncLoad(scene: Phaser.Scene, loadFunc: (scene: Phaser.Scene) => unknown) {
  return new Promise((resolve) => {
    loadFunc(scene);
    scene.load.once('complete', resolve);
    scene.load.start();
  });
}

export function steppedCubicEase(duration: number, frameRate = 10) {
  const frameDuration = 1000 / frameRate;
  const steps = duration / frameDuration;
  return (v: number) => {
    return Phaser.Math.Easing.Cubic.Out(Phaser.Math.Easing.Stepped(v, steps));
  };
}

export function steppedEase(steps: number) {
  return (v: number) => Phaser.Math.Easing.Stepped(v, steps);
}

interface PointerEventHandlers {
  hover?: () => unknown;
  activate?: () => unknown;
  deactivate?: () => unknown;
  click?: () => unknown;
}

export function onPointer(gameObject: Phaser.GameObjects.GameObject, handlers: PointerEventHandlers) {
  let clicking = false;
  gameObject.on('pointerdown', () => {
    handlers.activate?.();
    clicking = true;
  });
  gameObject.on('pointerout', () => {
    if (clicking) {
      handlers.deactivate?.();
    }
    clicking = false;
  });
  gameObject.on('pointerup', () => {
    if (clicking) {
      clicking = false;
      handlers.deactivate?.();
      handlers.click?.();
    }
  });
  gameObject.on('pointerover', () => {
    handlers.hover?.();
  });
}

export function clamp(min: number, value: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

export function centerOf(
  object: Phaser.Cameras.Scene2D.BaseCamera | Phaser.GameObjects.Components.GetBounds,
): [number, number] {
  if (object instanceof Phaser.Cameras.Scene2D.BaseCamera) {
    return [object.centerX, object.centerY];
  }

  const center = object.getCenter<Phaser.Math.Vector2>();
  return [center.x, center.y];
}

export function range(start: number, end?: number, step = 1) {
  if (end === undefined) {
    end = start;
    start = 0;
  }

  const list = [];
  for (let k = start; k < end; k += step) {
    list.push(k);
  }

  return list;
}

export function fillArray<T>(length: number, makeValue: (index: number) => T): T[] {
  const array = [];
  for (let k = 0; k < length; k++) {
    array.push(makeValue(k));
  }
  return array;
}

export function fillMatrix<T>(width: number, height: number, makeValue: (x: number, y: number) => T): T[][] {
  const matrix = [];
  for (let y = 0; y < height; y++) {
    const row = [];
    for (let x = 0; x < width; x++) {
      row.push(makeValue(x, y));
    }
    matrix.push(row);
  }
  return matrix;
}

export function defaultHitProcess(
  _:
    | Phaser.Types.Physics.Arcade.GameObjectWithBody
    | Phaser.Tilemaps.Tile
    | Phaser.Physics.Arcade.Body
    | Phaser.Physics.Arcade.StaticBody,
  other:
    | Phaser.Types.Physics.Arcade.GameObjectWithBody
    | Phaser.Tilemaps.Tile
    | Phaser.Physics.Arcade.Body
    | Phaser.Physics.Arcade.StaticBody,
) {
  if (other instanceof Phaser.Tilemaps.Tile) {
    return other.getCollisionGroup() !== null;
  }
  return true;
}

export function wouldHit(
  scene: Phaser.Scene,
  gameObject: Phaser.Types.Physics.Arcade.GameObjectWithBody,
  collider2: Phaser.Types.Physics.Arcade.ArcadeColliderType,
  dx = 0,
  dy = 0,
  collideCallback?: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
  processCallback: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback = defaultHitProcess,
) {
  gameObject.body.position.x += dx;
  gameObject.body.position.y += dy;
  const hit = scene.physics.overlap(gameObject, collider2, collideCallback, processCallback);
  gameObject.body.position.x -= dx;
  gameObject.body.position.y -= dy;
  return hit;
}

export function getTiledProperty<T>(
  object: Phaser.Types.Tilemaps.TiledObject | Phaser.Tilemaps.LayerData,
  name: string,
): T {
  return object.properties?.find?.((property: { name: string }) => property.name === name)?.value;
}

export function asyncPlaySound(sound: Phaser.Sound.BaseSound) {
  return new Promise((resolve) => {
    sound.once('complete', resolve).play();
  });
}

/** Convert frames to milliseconds at a given fps. */
export function frameMs(frames: number, fps = 60) {
  return Math.floor((1000 / fps) * frames);
}

/**
 * Executes a generator function that yields promises, but aborts execution if any of
 * the given game objects is destroyed while awaiting a yielded promise.
 */
export async function withGameObjects(
  gameObjects: Phaser.GameObjects.GameObject[],
  generatorFunc: () => Generator<Promise<any>>,
) {
  const generator = generatorFunc();
  let { value, done } = generator.next();
  while (true) {
    if (done) {
      break;
    }

    const nextParam = await value;
    for (const gameObject of gameObjects) {
      if (gameObject.isDestroyed) {
        break;
      }
    }

    const result = generator.next(nextParam);
    done = result.done;
    value = result.value;
  }
}

export interface MenuItem {
  key: string;
  isCancel?: boolean;
  [Direction.Up]?: string;
  [Direction.Down]?: string;
  [Direction.Left]?: string;
  [Direction.Right]?: string;
}

export interface MenuControls {
  up: ControllerButton;
  down: ControllerButton;
  left: ControllerButton;
  right: ControllerButton;
  confirm: ControllerButton;
  cancel: ControllerButton;
}

export class Menu extends Phaser.Events.EventEmitter {
  protected cursor: string | null = null;
  protected inputPaused = false;

  constructor(
    protected scene: Phaser.Scene,
    protected controls: MenuControls,
    protected menuItems: { [key: string]: MenuItem } = {},
  ) {
    super();
    this.resetCursor();
  }

  resetCursor() {
    const menuItemKeys = Object.keys(this.menuItems);
    if (menuItemKeys.length > 0) {
      this.moveCursorTo(menuItemKeys[0]);
    }
    return this;
  }

  setMenuItems(menuItems: { [key: string]: MenuItem }, resetCursor = true) {
    this.menuItems = menuItems;

    if (resetCursor) {
      this.moveCursorTo(Object.keys(menuItems)[0]);
    }

    return this;
  }

  currentItem() {
    return this.cursor ? this.menuItems[this.cursor] : null;
  }

  moveCursorTo(key: string) {
    const oldCursor = this.cursor;
    this.cursor = key;
    this.emit('focus', this.cursor, this.currentItem(), oldCursor, this);
  }

  moveCursorInDirection(direction: Direction) {
    const currentItem = this.currentItem();
    if (currentItem && currentItem[direction] !== undefined) {
      this.moveCursorTo(currentItem[direction] as string);
    }
  }

  pauseInput() {
    this.inputPaused = true;
    return this;
  }

  resumeInput() {
    this.inputPaused = false;
    return this;
  }

  update() {
    if (this.inputPaused) {
      return;
    }

    if (this.controls.confirm.justDown) {
      if (this.currentItem()?.isCancel) {
        this.emit('cancel', this.cursor);
      } else {
        this.emit('select', this.cursor, this.currentItem());
      }
    } else if (this.controls.cancel.justDown) {
      this.emit('cancel', this.cursor);
    } else if (this.controls.up.justDown) {
      this.moveCursorInDirection(Direction.Up);
    } else if (this.controls.down.justDown) {
      this.moveCursorInDirection(Direction.Down);
    } else if (this.controls.left.justDown) {
      this.moveCursorInDirection(Direction.Left);
    } else if (this.controls.right.justDown) {
      this.moveCursorInDirection(Direction.Right);
    }
  }
}

export function verticalMenuItems<T extends MenuItem>(itemList: T[]) {
  return itemList.reduce<{ [key: string]: T }>((acc, menuItem, index) => {
    menuItem[Direction.Up] = index === 0 ? itemList[itemList.length - 1].key : itemList[index - 1].key;
    menuItem[Direction.Down] = index === itemList.length - 1 ? itemList[0].key : itemList[index + 1].key;
    acc[menuItem.key] = menuItem;
    return acc;
  }, {});
}

export function horizontalMenuItems<T extends MenuItem>(itemList: T[]) {
  return itemList.reduce<{ [key: string]: T }>((acc, menuItem, index) => {
    menuItem[Direction.Left] = index === 0 ? itemList[itemList.length - 1].key : itemList[index - 1].key;
    menuItem[Direction.Right] = index === itemList.length - 1 ? itemList[0].key : itemList[index + 1].key;
    acc[menuItem.key] = menuItem;
    return acc;
  }, {});
}

export interface TextMenuItem extends MenuItem {
  text: string;
  x: number;
  y: number;
}

export class TextMenu extends Menu {
  protected bitmapTexts: { [key: string]: Phaser.GameObjects.BitmapText } = {};

  constructor(
    scene: Phaser.Scene,
    protected font: string,
    protected cursorPrefix: string,
    controls: MenuControls,
    protected menuItems: { [key: string]: TextMenuItem } = {},
  ) {
    super(scene, controls, menuItems);

    for (const [key, { x, y, text }] of Object.entries(menuItems)) {
      this.bitmapTexts[key] = this.scene.add.bitmapText(x, y, font, `${' '.repeat(cursorPrefix.length)}${text}`);
    }

    this.on('focus', (cursor: string, _menuItem: TextMenuItem, oldCursor: string) => {
      this.setBitmapTextPrefix(cursor, cursorPrefix);
      if (oldCursor !== cursor) {
        this.setBitmapTextPrefix(oldCursor, ' '.repeat(cursorPrefix.length));
      }
    });
  }

  protected setBitmapTextPrefix(key: string, prefix: string) {
    const bitmapText = this.bitmapTexts[key];
    bitmapText?.setText(`${prefix}${bitmapText.text.slice(this.cursorPrefix.length)}`);
  }

  setMenuItems(menuItems: { [key: string]: TextMenuItem }, resetCursor = true) {
    for (const bitmapText of Object.values(this.bitmapTexts)) {
      bitmapText.destroy();
    }
    this.bitmapTexts = {};

    for (const [key, { x, y, text }] of Object.entries(menuItems)) {
      this.bitmapTexts[key] = this.scene.add.bitmapText(
        x,
        y,
        this.font,
        `${' '.repeat(this.cursorPrefix.length)}${text}`,
      );
    }

    return super.setMenuItems(menuItems, resetCursor);
  }

  setMenuItemText(key: string, text: string) {
    const bitmapText = this.bitmapTexts[key];
    bitmapText.setText(`${key === this.cursor ? this.cursorPrefix : ''}${text}`);
  }

  setVisible(value: boolean) {
    for (const bitmapText of Object.values(this.bitmapTexts)) {
      bitmapText.setVisible(value);
    }
    return this;
  }

  setDepth(value: number) {
    for (const bitmapText of Object.values(this.bitmapTexts)) {
      bitmapText.setDepth(value);
    }
    return this;
  }

  destroy() {
    for (const bitmapText of Object.values(this.bitmapTexts)) {
      bitmapText.destroy();
    }
  }
}
